import { useEffect } from 'react'
import { isNativeApp } from '../utils/platform'
import { pushBackHandler, runBackHandlers, type BackHandler } from '../utils/android-back'
import { decideCallAwareBack } from '../utils/call-back-policy'
import { getVoiceCallSession, patchVoiceCallSession } from '../stores/voice-call-store'

/**
 * Mount ONCE near the app root. Registers the single Capacitor back-button
 * listener that drives the shared handler stack. When no handler consumes the
 * event we fall back to in-app history navigation, exiting only when there is
 * nowhere left to go — and never exiting while a call is live (see
 * decideCallAwareBack).
 */
export function useAndroidBackButtonRoot(): void {
  useEffect(() => {
    if (!isNativeApp()) return
    let remove = () => {}
    let active = true

    void (async () => {
      try {
        const { App } = await import('@capacitor/app')
        const handle = await App.addListener('backButton', ({ canGoBack }) => {
          const callAction = decideCallAwareBack(getVoiceCallSession())
          if (callAction === 'minimize-call-overlay') {
            patchVoiceCallSession({ userMinimized: true })
            return
          }
          if (callAction === 'minimize-app') {
            void App.minimizeApp()
            return
          }
          if (runBackHandlers()) return
          if (canGoBack) {
            window.history.back()
          } else if (callAction === 'in-call') {
            void App.minimizeApp()
          } else {
            void App.exitApp()
          }
        })
        if (!active) {
          void handle.remove()
          return
        }
        remove = () => void handle.remove()
      } catch {
        /* @capacitor/app unavailable (web) — no-op */
      }
    })()

    return () => {
      active = false
      remove()
    }
  }, [])
}

/**
 * Register a scoped back handler while `active` is true. The handler consumes
 * the hardware back press (e.g. close an open chat thread / modal) instead of
 * letting it navigate routes.
 */
export function useBackHandler(active: boolean, onBack: () => void): void {
  useEffect(() => {
    if (!active) return
    const handler: BackHandler = () => {
      onBack()
      return true
    }
    return pushBackHandler(handler)
  }, [active, onBack])
}
